import { useCallback } from 'react';
import toast from 'react-hot-toast';
import { getPaymentMethodService } from '../service/ServiceRegistry';
import type { FetchDataFn, TableDataResponse, TableQueryParams } from '../types/table';
import type {
  PaymentMethod,
  PaymentMethodCreateRequest,
  PaymentMethodUpdateRequest,
  PaymentMethodQueryParams,
  PaymentMethodsData,
} from '../types/api/payment-method';

export function usePaymentMethods() {
  const paymentMethodService = getPaymentMethodService();

  const fetchPaymentMethods: FetchDataFn<PaymentMethod> = useCallback(
    async (params: TableQueryParams): Promise<TableDataResponse<PaymentMethod>> => {
      const apiParams: PaymentMethodQueryParams = {
        page: params.page,
        size: params.pageSize,
      };

      const response: PaymentMethodsData = await paymentMethodService.getPaymentMethods(apiParams);

      return {
        data: response.items,
        pagination: {
          page: response.page,
          pageSize: response.size,
          total: response.totalItems,
          totalPages: response.totalPages,
          hasNext: response.hasNext,
          hasPrev: response.hasPrevious,
        },
      };
    },
    [paymentMethodService]
  );

  return { fetchPaymentMethods };
}

export function usePaymentMethodActions() {
  const paymentMethodService = getPaymentMethodService();

  const createWithFiles = useCallback(
    async (
      payload: PaymentMethodCreateRequest,
      logoFile?: File,
      qrImageFile?: File
    ): Promise<PaymentMethod> => {
      try {
        const pm = await paymentMethodService.createWithFiles(payload, logoFile, qrImageFile);
        toast.success('Payment method created successfully');
        return pm;
      } catch (error: any) {
        toast.error(error.message || 'Failed to create payment method');
        throw error;
      }
    },
    [paymentMethodService]
  );

  const createWithJson = useCallback(
    async (payload: PaymentMethodCreateRequest): Promise<PaymentMethod> => {
      try {
        const pm = await paymentMethodService.createWithJson(payload);
        toast.success('Payment method created successfully');
        return pm;
      } catch (error: any) {
        toast.error(error.message || 'Failed to create payment method');
        throw error;
      }
    },
    [paymentMethodService]
  );

  const updateWithFiles = useCallback(
    async (
      paymentMethodId: string,
      payload: PaymentMethodUpdateRequest,
      logoFile?: File,
      qrImageFile?: File
    ): Promise<PaymentMethod> => {
      try {
        const pm = await paymentMethodService.updateWithFiles(
          paymentMethodId,
          payload,
          logoFile,
          qrImageFile
        );
        toast.success('Payment method updated successfully');
        return pm;
      } catch (error: any) {
        toast.error(error.message || 'Failed to update payment method');
        throw error;
      }
    },
    [paymentMethodService]
  );

  const updateWithJson = useCallback(
    async (paymentMethodId: string, payload: PaymentMethodUpdateRequest): Promise<PaymentMethod> => {
      try {
        const pm = await paymentMethodService.updateWithJson(paymentMethodId, payload);
        toast.success('Payment method updated successfully');
        return pm;
      } catch (error: any) {
        toast.error(error.message || 'Failed to update payment method');
        throw error;
      }
    },
    [paymentMethodService]
  );

  const activate = useCallback(
    async (paymentMethodId: string): Promise<void> => {
      try {
        await paymentMethodService.activate(paymentMethodId);
        toast.success('Payment method activated');
      } catch (error: any) {
        toast.error(error.message || 'Failed to activate payment method');
        throw error;
      }
    },
    [paymentMethodService]
  );

  const deactivate = useCallback(
    async (paymentMethodId: string): Promise<void> => {
      try {
        await paymentMethodService.deactivate(paymentMethodId);
        toast.success('Payment method deactivated');
      } catch (error: any) {
        toast.error(error.message || 'Failed to deactivate payment method');
        throw error;
      }
    },
    [paymentMethodService]
  );

  const deletePaymentMethod = useCallback(
    async (paymentMethodId: string): Promise<void> => {
      try {
        await paymentMethodService.delete(paymentMethodId);
        toast.success('Payment method deleted');
      } catch (error: any) {
        toast.error(error.message || 'Failed to delete payment method');
        throw error;
      }
    },
    [paymentMethodService]
  );

  return {
    createWithFiles,
    createWithJson,
    updateWithFiles,
    updateWithJson,
    activate,
    deactivate,
    deletePaymentMethod,
  };
}

